import { ServerCog } from 'lucide-react';

import { useOptionalEnvironment } from '../context/EnvironmentContext';
import { IDENTIFIER, STATUS, STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-422 / FD-ADR-040 §8 — "this acts on THAT deployment", said inside the dialog
// that is about to do it.
//
// This is the innermost of the four defences FD-421 made necessary by putting N
// environments behind one login. The selector says where you are; the badge says
// which tone; the drift banner says the two sources disagree. This one is the
// last thing an operator reads before clicking a button that revokes a
// credential, removes devices from a campaign, or empties a deployment — and it
// is the only one of the four that appears in their field of view at that moment.
//
// ── IT FOLLOWS THE SELECTOR (FD-501, owner decision) ──────────────────────
//
// The echo renders only when the console serves MORE THAN ONE environment — the
// same predicate `EnvironmentSelector` already uses to decide whether to render
// at all. On a single-environment console it returns null.
//
// This file previously argued the opposite at length, and that argument is
// answered rather than ignored. Its own subject is DISAMBIGUATION: FD-ADR-040 §8
// added this because putting N environments behind one login manufactures the
// hazard of "acting on production believing you are on staging". With one
// environment there is nothing to have chosen wrongly, so the row names something
// the operator could not have got wrong — and the owner reasonably read a line
// present in ten dialogs that can only ever say one thing as noise.
//
// The two reasons the old comment gave are weaker than they read:
//
//   * A `config.json` generated for the wrong deployment points a
//     single-environment console at the wrong Manager. True — and
//     `EnvironmentDriftBanner` detects exactly that, ALWAYS, by comparing the
//     config label against the server's own `ENVIRONMENT_NAME` from `whoami`.
//     That is a persistent banner rather than a line inside a dialog somebody
//     has to open, so the detection is not lost; only a second, weaker copy of
//     it is.
//   * Habit formation, so a Mode 1 operator has learned to look for the row
//     before their fleet grows a second environment. Real, but it is a reason
//     to show a control that does nothing today — which is the one thing
//     FD-ADR-043 D4 refuses everywhere else in this console.
//
// WHAT IS NOT NARROWED: the moment a fleet has two environments, every surface
// in `EnvironmentEcho.coverage.test.ts` gets the echo back, unchanged, including
// the production tone. The coverage guard still scans for the call site, so a
// destructive dialog cannot quietly drop it; `EnvironmentEcho.dialogs.test.tsx`
// renders a TWO-environment config so the behavioural half is still proven.

/**
 * `production` speaks up; everything else is neutral.
 *
 * Amber, never red — the console's status vocabulary reads red as "this is
 * broken" and amber as "a human should be paying attention" (FLUENT_UI_ADDENDUM).
 * Being on production is not a failure; it is the fact to notice before acting.
 * The same rule `EnvironmentSelector.toneAppearance` applies, spelled once more
 * here because this component paints its own element rather than a Fluent Badge.
 */
function isEmphatic(tone: string | undefined): boolean {
  return (tone ?? '').trim().toLowerCase() === 'production';
}

/** `https://host/` → `host`. Falls back to the raw value rather than throwing. */
function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

export interface EnvironmentEchoProps {
  /**
   * Overrides the lead-in. Defaults to "This acts on".
   *
   * Used where the surrounding copy would otherwise read oddly — a dialog that
   * has already said "this cannot be undone" wants "Acting on", not a second
   * sentence starting "This".
   */
  label?: string;
}

export default function EnvironmentEcho({ label }: EnvironmentEchoProps) {
  const context = useOptionalEnvironment();
  // No provider, nothing to name. Unreachable in the real app — `App` renders
  // inside `EnvironmentProvider`, and `api/client.ts` throws by name on a request
  // made outside it, so a console in that shape cannot load a page at all. See
  // `useOptionalEnvironment` for why this tolerance exists and what stops it
  // mattering.
  if (!context) return null;

  // Nothing to disambiguate — see the header. Deliberately BEFORE the tone check:
  // a single-environment console's production badge lives in the top bar
  // (`EnvironmentSelector` renders it whenever `tone` is set, at any count), so
  // that signal is not lost by returning null here.
  //
  // FD-586 — keyed on what the operator can SEE, not on what the fleet holds.
  // A customer admin who may reach one environment of fifty never chose between
  // them, so the echo would name something they could not have got wrong: the
  // same argument FD-501 made for a one-environment console, now per tenant.
  // The selector collapses on the same predicate, so the two stay in step.
  if (!context.hasEnvironmentChoice) return null;

  const { environment } = context;
  const emphatic = isEmphatic(environment.tone);
  const accent = emphatic ? STATUS.warning : STROKE.base;
  const host = hostOf(environment.managerBaseUrl);

  return (
    <div
      role="note"
      className="flex items-center gap-2 px-3 py-2"
      style={{
        backgroundColor: emphatic ? STATUS.warningTint : SURFACE.rowFill,
        border: `1px solid ${STROKE.base}`,
        // The left edge is the only thing that changes between tones, so the
        // component reads the same everywhere and the difference is legible
        // without being a second warning banner competing with the dialog's own.
        borderLeft: `3px solid ${accent}`,
        borderRadius: '2px',
      }}
    >
      <ServerCog
        size={15}
        strokeWidth={1.5}
        color={emphatic ? STATUS.warning : TEXT.secondary}
        aria-hidden="true"
      />
      <span className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
        {label ?? 'This acts on'}{' '}
        <span className="font-semibold" style={{ color: TEXT.primary }}>
          {environment.label}
        </span>
      </span>
      {/* The Manager origin, in mono because it is an identifier and because it
          is what an operator compares against when a label looks wrong. Titled
          rather than wrapped: a long azurewebsites host would otherwise push a
          narrow dialog around. */}
      <span
        className="ml-auto max-w-[240px] truncate text-[11px]"
        style={{ ...IDENTIFIER, color: TEXT.secondary }}
        title={environment.managerBaseUrl}
      >
        {host}
      </span>
    </div>
  );
}
